import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import { Plus, SquarePen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllPressData } from '../../Services/PressManagement/PressApi';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const GetPressData = () => {
    const navigate = useNavigate();
    // eslint-disable-next-line no-unused-vars
    const { showSnackbar } = useGlobalSnackbar();
    const [pressData, setPressData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchPressData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllPressData();

            if (response && response.success) {
                if (response.data) {
                    // API returns single object → convert to array
                    setPressData([response.data]);
                } else {
                    setPressData([]);
                }
            } else {
                setPressData([]);
                setError(response?.message || "Failed to load press data.");
            }
        } catch (err) {
            console.error(err);
            setError("Failed to fetch press data. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPressData();
    }, [fetchPressData]);

    const handleAddClick = () => {
        if (pressData.length >= 1) {
            showSnackbar("Only 1 Press record is allowed. You can edit the existing record.", "warning");
            return;
        }
        navigate('/press-management/add');
    };

    const handleEditClick = (press) => {
        navigate('/press-management/edit', { state: { press } });
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>

                {/* Header */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 4 }}
                >
                    <Typography
                        sx={{
                            color: '#222', fontWeight: 500, fontSize: { xs: '1.75rem', md: '1.8rem' },
                        }}
                    >
                        Press Management
                    </Typography>

                    <Stack direction="row" spacing={2}>
                        <PermissionWrapper permission="view" mode="disable">
                            <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                                variant="outlined" onClick={() => navigate('/press-management/news')}
                                sx={{ px: 3, color: '#FFBD65', borderRadius: '8px', }}
                            >
                                Press News
                            </Button>
                        </PermissionWrapper>
                        {pressData.length === 0 && (
                            <PermissionWrapper permission="create" mode="disable">
                                <Button disableFocusRipple disableRipple disableTouchRipple
                                    variant="contained" startIcon={<Plus size={18} />}
                                    onClick={handleAddClick} disableElevation
                                    sx={{ bgcolor: '#FFBD65', px: 3, color: '#fff', borderRadius: '8px' }}
                                >
                                    Add Press
                                </Button>
                            </PermissionWrapper>
                        )}
                    </Stack>
                </Stack>

                {/* Error */}
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                {/* Table */}
                <Paper
                    sx={{
                        borderRadius: "8px",
                        border: "1px solid #E8E8E8",
                        overflow: "hidden"
                    }}
                    elevation={0}
                >
                    <TableContainer>
                        <Table sx={{ minWidth: 1050 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap', textAlign: 'center' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Header Image</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Links</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                            <CircularProgress sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : pressData.length > 0 ? (
                                    pressData.map((press, idx) => (
                                        <TableRow key={press.id} >
                                            <TableCell sx={{ textAlign: 'center' }}>{idx + 1}</TableCell>

                                            <TableCell>
                                                <Avatar
                                                    src={press.headerImage}
                                                    variant="rounded"
                                                    sx={{
                                                        width: 60,
                                                        height: 60,
                                                        border: '1px solid #EEE'
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell sx={{ fontWeight: 500 }}>
                                                {press.email}
                                            </TableCell>

                                            <TableCell>
                                                <Typography
                                                    component="a"
                                                    href={press.yogaInfoLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        display: 'block',
                                                        color: '#FFBD65',
                                                        fontSize: '0.85rem',
                                                        textDecoration: 'none',
                                                        '&:hover': { textDecoration: 'underline' }
                                                    }}
                                                >
                                                    Yoga Info
                                                </Typography>

                                                <Typography
                                                    component="a"
                                                    href={press.pressReleaseLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        display: 'block',
                                                        color: '#FFBD65',
                                                        fontSize: '0.85rem',
                                                        textDecoration: 'none',
                                                        '&:hover': { textDecoration: 'underline' }
                                                    }}
                                                >
                                                    Press Release
                                                </Typography>

                                                <Typography
                                                    component="a"
                                                    href={press.brandAssetsLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        display: 'block',
                                                        color: '#FFBD65',
                                                        fontSize: '0.85rem',
                                                        textDecoration: 'none',
                                                        '&:hover': { textDecoration: 'underline' }
                                                    }}
                                                >
                                                    Brand Assets
                                                </Typography>
                                            </TableCell>

                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {press.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {press.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(press.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(press.updatedAt)}
                                            </TableCell>

                                            <TableCell align="right">
                                                <PermissionWrapper permission="edit" mode="disable">
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            sx={{ color: '#444' }}
                                                            onClick={() => handleEditClick(press)}
                                                        >
                                                            <SquarePen size={18} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </PermissionWrapper>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                            No press data found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>

                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </PermissionWrapper>
    );
};

export default GetPressData;
